import React, { useEffect, useState } from "react";
import InfoPage from ".";

const readIsLoggedIn = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(
      window.localStorage.getItem("postaway_auth_token") ||
        window.localStorage.getItem("postaway_email")
    );
  } catch (_) {
    return false;
  }
};

// Mirrors the categories on the home-page Partner tab. Keep in sync with
// `PARTNER_CATEGORIES` in src/screens/Partners/index.js and
// `ALLOWED_PARTNER_CATEGORIES` in api/partner/apply.mjs.
const PARTNER_CATEGORIES = [
  "Marketing",
  "Design",
  "Legal",
  "Mentorship",
  "Real Estate",
  "Amenities",
  "Management",
  "Services",
];
const MAX_PARTNER_CATEGORIES = 3;

const emptyForm = {
  businessName: "",
  contactName: "",
  email: "",
  phone: "",
  websiteUrl: "",
  instagram: "",
  locationCity: "",
  locationState: "",
  businessDescription: "",
  helpText: "",
  categories: [],
};

const PartnerSignup = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(readIsLoggedIn());
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleCategory = (category) => {
    setForm((f) => {
      const isSelected = f.categories.includes(category);
      if (isSelected) {
        return { ...f, categories: f.categories.filter((c) => c !== category) };
      }
      if (f.categories.length >= MAX_PARTNER_CATEGORIES) {
        return f;
      }
      return { ...f, categories: [...f.categories, category] };
    });
  };

  const normalizeUrl = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form, websiteUrl: normalizeUrl(form.websiteUrl) };
      const res = await fetch("/api/partner/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setSubmitted(true);
        setForm(emptyForm);
      } else {
        throw new Error(data?.error || "send_failed");
      }
    } catch (err) {
      console.error("Partner apply error:", err);
      setError("Something went wrong sending your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const below = submitted ? (
    <div className="partner-signup-page">
      <div className="partner-signup-card partner-signup-thanks">
        <div className="partner-signup-check" aria-hidden="true">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12.5l2.5 2.5L16 9.5" />
          </svg>
        </div>
        <h2 className="partner-signup-title">Thanks — we got your application!</h2>
        <p className="partner-signup-description">
          We'll review your application within 24 hours and email you at the
          address you provided with next steps. If you're approved, we'll
          include a secure link to choose your Preferred Partner plan and
          complete billing.
        </p>
        <div className="partner-signup-thanks-actions">
          {isLoggedIn && (
            <a className="partner-signup-btn primary" href="/partners">Browse partners</a>
          )}
          <a className="partner-signup-btn secondary" href="/contact">Questions? Contact us</a>
        </div>
      </div>
      <PartnerSignupStyles />
    </div>
  ) : (
    <div className="partner-signup-page">
      <div className="partner-signup-intro">
        <h2 className="partner-signup-title">Apply to be a Preferred Partner</h2>
        <p className="partner-signup-description">
          Postaway Preferred Partners get in front of thousands of creators,
          hosts, and travelers. Tell us about your business below — we'll
          review your application within 24 hours and send you a link to
          choose a plan if approved.
        </p>
        <ul className="partner-signup-benefits">
          <li>Featured placement on the Partners directory</li>
          <li>Direct messaging with creators, hosts, and travelers</li>
          <li>Promote an exclusive discount code to Postaway members</li>
          <li>Showcase your website, socials, and business story</li>
        </ul>
      </div>

      <form className="partner-signup-form" onSubmit={handleSubmit}>
        <div className="partner-signup-field">
          <label htmlFor="ps-business">
            Business name <span className="required">*</span>
          </label>
          <input
            id="ps-business"
            type="text"
            required
            value={form.businessName}
            onChange={update("businessName")}
            placeholder="Glacier Outfitters"
          />
        </div>

        <div className="partner-signup-row">
          <div className="partner-signup-field">
            <label htmlFor="ps-contact">
              Your name <span className="required">*</span>
            </label>
            <input
              id="ps-contact"
              type="text"
              required
              value={form.contactName}
              onChange={update("contactName")}
              placeholder="Jane Smith"
            />
          </div>
          <div className="partner-signup-field">
            <label htmlFor="ps-email">
              Email <span className="required">*</span>
            </label>
            <input
              id="ps-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              placeholder="jane@example.com"
            />
          </div>
        </div>

        <div className="partner-signup-row">
          <div className="partner-signup-field">
            <label htmlFor="ps-phone">Phone (optional)</label>
            <input
              id="ps-phone"
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              placeholder="(555) 123-4567"
            />
          </div>
          <div className="partner-signup-field">
            <label htmlFor="ps-website">
              Website <span className="required">*</span>
            </label>
            <input
              id="ps-website"
              type="text"
              required
              value={form.websiteUrl}
              onChange={update("websiteUrl")}
              placeholder="example.com"
            />
          </div>
        </div>

        <div className="partner-signup-row">
          <div className="partner-signup-field">
            <label htmlFor="ps-city">City</label>
            <input
              id="ps-city"
              type="text"
              value={form.locationCity}
              onChange={update("locationCity")}
              placeholder="Bozeman"
            />
          </div>
          <div className="partner-signup-field">
            <label htmlFor="ps-state">State</label>
            <input
              id="ps-state"
              type="text"
              value={form.locationState}
              onChange={update("locationState")}
              placeholder="MT"
            />
          </div>
        </div>

        <div className="partner-signup-field">
          <label htmlFor="ps-instagram">Instagram handle (optional)</label>
          <input
            id="ps-instagram"
            type="text"
            value={form.instagram}
            onChange={update("instagram")}
            placeholder="@glacieroutfitters"
          />
        </div>

        <div className="partner-signup-field">
          <label htmlFor="ps-categories">
            Which categories best describe your business?
          </label>
          <p className="partner-signup-hint">
            Choose up to {MAX_PARTNER_CATEGORIES}. These match the categories
            members can browse on the Partners page.
          </p>
          <div
            id="ps-categories"
            className="partner-signup-categories"
            role="group"
            aria-label="Business categories"
          >
            {PARTNER_CATEGORIES.map((category) => {
              const isSelected = form.categories.includes(category);
              const isAtLimit =
                !isSelected && form.categories.length >= MAX_PARTNER_CATEGORIES;
              return (
                <button
                  key={category}
                  type="button"
                  className={`partner-signup-category-chip${
                    isSelected ? " selected" : ""
                  }${isAtLimit ? " disabled" : ""}`}
                  aria-pressed={isSelected}
                  disabled={isAtLimit}
                  onClick={() => toggleCategory(category)}
                >
                  {category}
                </button>
              );
            })}
          </div>
          <p className="partner-signup-categories-count">
            {form.categories.length}/{MAX_PARTNER_CATEGORIES} selected
          </p>
        </div>

        <div className="partner-signup-field">
          <label htmlFor="ps-description">Tell us about your business</label>
          <textarea
            id="ps-description"
            value={form.businessDescription}
            onChange={update("businessDescription")}
            placeholder="What do you sell / offer?"
            rows={4}
          />
        </div>

        <div className="partner-signup-field">
          <label htmlFor="ps-help">How can you help Postaway hosts &amp; creators?</label>
          <textarea
            id="ps-help"
            value={form.helpText}
            onChange={update("helpText")}
            placeholder="e.g. product partnerships, gear rentals, bulk pricing, co-marketing."
            rows={3}
          />
        </div>

        {error && <div className="partner-signup-error">{error}</div>}

        <div className="partner-signup-actions">
          <button type="submit" className="partner-signup-btn primary" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </div>

        <p className="partner-signup-footer">
          Already a partner? <a href="/partner-login">Log in</a>
        </p>
      </form>

      <PartnerSignupStyles />
    </div>
  );

  return <InfoPage below={below} disableStamps />;
};

const PartnerSignupStyles = () => (
  <style>{`
    .partner-signup-page {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 760px;
      margin: 0 auto;
      padding: 24px 24px 56px;
    }
    @media (max-width: 768px) {
      .partner-signup-page { padding: 12px 16px 40px; max-width: 100%; }
    }
    .partner-signup-intro {
      text-align: center;
      margin-bottom: 28px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }
    .partner-signup-title {
      font-family: 'Sailors', serif;
      font-size: 32px;
      color: #0f084b;
      margin: 0;
      line-height: 1.2;
      text-align: center;
    }
    @media (max-width: 768px) {
      .partner-signup-title { font-size: 24px; }
    }
    .partner-signup-description {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 16px;
      color: #353945;
      line-height: 1.55;
      max-width: 600px;
      margin: 0;
    }
    @media (max-width: 768px) {
      .partner-signup-description { font-size: 14px; }
    }
    .partner-signup-benefits {
      list-style: none;
      padding: 0;
      margin: 4px 0 0;
      width: fit-content;
      max-width: 520px;
      text-align: left;
      display: grid;
      gap: 10px;
    }
    .partner-signup-benefits li {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #0f084b;
      padding-left: 22px;
      position: relative;
      line-height: 1.5;
    }
    .partner-signup-benefits li::before {
      content: '✓';
      color: #dc1d08;
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 700;
    }
    .partner-signup-form {
      background: #fff;
      border: 1px solid #E6E8EC;
      border-radius: 16px;
      padding: 28px 28px 24px;
      box-shadow: 0 6px 24px rgba(15, 8, 75, 0.06);
      display: grid;
      gap: 16px;
    }
    @media (max-width: 768px) {
      .partner-signup-form { padding: 20px 16px 20px; }
    }
    .partner-signup-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    @media (max-width: 640px) {
      .partner-signup-row { grid-template-columns: 1fr; }
    }
    .partner-signup-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .partner-signup-field label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #0f084b;
    }
    .partner-signup-field .required { color: #dc1d08; }
    .partner-signup-field input,
    .partner-signup-field textarea {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #111827;
      border: 1px solid #d1d5db;
      border-radius: 10px;
      padding: 10px 12px;
      background: #fff;
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .partner-signup-field textarea { resize: vertical; min-height: 84px; }
    .partner-signup-field input:focus,
    .partner-signup-field textarea:focus {
      border-color: #0f084b;
      box-shadow: 0 0 0 3px rgba(15, 8, 75, 0.12);
    }
    .partner-signup-hint {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      line-height: 1.45;
      color: #6b6f80;
      margin: 2px 0 6px;
    }
    .partner-signup-categories {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 4px;
    }
    .partner-signup-category-chip {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 500;
      padding: 8px 14px;
      border-radius: 999px;
      border: 1px solid #E6E8EC;
      background: #fff;
      color: #0f084b;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
    }
    .partner-signup-category-chip:hover:not(:disabled) {
      border-color: #0f084b;
    }
    .partner-signup-category-chip.selected {
      background: #0f084b;
      border-color: #0f084b;
      color: #ffffff;
    }
    .partner-signup-category-chip.disabled,
    .partner-signup-category-chip:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .partner-signup-categories-count {
      margin: 6px 0 0;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      color: #6b6f80;
    }
    .partner-signup-error {
      background: #fff1f0;
      border: 1px solid #fecaca;
      color: #991b1b;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 14px;
    }
    .partner-signup-actions {
      display: flex;
      justify-content: center;
      margin-top: 6px;
    }
    .partner-signup-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 600;
      text-decoration: none;
      border: 2px solid transparent;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 220px;
    }
    @media (max-width: 768px) {
      .partner-signup-btn { width: 100%; min-height: 44px; min-width: 0; }
    }
    .partner-signup-btn.primary {
      background: #dc1d08;
      color: #fff;
      border-color: #dc1d08;
    }
    .partner-signup-btn.primary:hover:not(:disabled) {
      background: #b51706;
      border-color: #b51706;
    }
    .partner-signup-btn.primary:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
    .partner-signup-btn.secondary {
      background: transparent;
      color: #0f084b;
      border-color: #0f084b;
    }
    .partner-signup-btn.secondary:hover {
      background: #0f084b;
      color: #fff;
    }
    .partner-signup-footer {
      text-align: center;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #353945;
      margin: 4px 0 0;
    }
    .partner-signup-footer a {
      color: #dc1d08;
      text-decoration: none;
      font-weight: 600;
    }
    .partner-signup-footer a:hover { text-decoration: underline; }

    .partner-signup-card {
      background: #fff;
      border: 1px solid #E6E8EC;
      border-radius: 16px;
      padding: 40px 40px 32px;
      box-shadow: 0 6px 24px rgba(15, 8, 75, 0.06);
      text-align: center;
    }
    @media (max-width: 768px) {
      .partner-signup-card { padding: 28px 20px 24px; }
    }
    .partner-signup-check {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #dc1d08;
      margin-bottom: 12px;
    }
    .partner-signup-thanks .partner-signup-title { margin-bottom: 8px; }
    .partner-signup-thanks-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
      margin-top: 20px;
    }
  `}</style>
);

export default PartnerSignup;
